/**
 * 24/7 Registered Nurse monthly evidence-report reminder.
 *
 * Aged Care Rules 2025 r 166-855(1): the report required under Act
 * s 166(1)(d) is due within 7 days after the end of each calendar month —
 * STATUTORY, verified in the constants register
 * (`packages/utils/src/award-data/compliance.ts` `RN_24_7_MONTHLY_REPORT_DAY`).
 *
 * The cron and worklist use the home timezone and home/month lodgement key.
 * The legacy UTC helper remains exported for historical callers and tests;
 * it must not drive a new statutory reminder.
 */
import { RN_24_7_MONTHLY_REPORT_DAY } from '../award-data/compliance';
import type { ComplianceDeadlineItem } from './types';

const PROVISION = 'Aged Care Rules 2025 r 166-855(1)';

export type Rn247Band = 'APPROACHING' | 'OVERDUE';

export interface Rn247Evaluation {
  item: ComplianceDeadlineItem;
  band: Rn247Band;
  dayOfMonth: number;
  daysOverdue: number;
  priorMonthLabel: string;
  periodKey: string;
}

function buildEvaluation(
  tenantId: string,
  dayOfMonth: number,
  priorMonth: Date,
  priorMonthLabel: string,
  labelTz: string,
  facilityId?: string,
): Rn247Evaluation {
  const band: Rn247Band = dayOfMonth > RN_24_7_MONTHLY_REPORT_DAY ? 'OVERDUE' : 'APPROACHING';
  const daysOverdue = Math.max(0, dayOfMonth - RN_24_7_MONTHLY_REPORT_DAY);
  const periodKey = `${priorMonth.getUTCFullYear()}-${String(priorMonth.getUTCMonth() + 1).padStart(2, '0')}`;
  return {
    band,
    dayOfMonth,
    daysOverdue,
    priorMonthLabel,
    periodKey,
    item: {
      id: facilityId
        ? `rn-24-7-monthly:${tenantId}:${facilityId}:${periodKey}`
        : `rn-24-7-monthly:${tenantId}:${periodKey}`,
      register: 'RN_24_7_MONTHLY_REPORT',
      recordRef: `${facilityId ?? tenantId}:${periodKey}`,
      title: '24/7 RN monthly evidence report',
      dueAt: new Date(
        Date.UTC(
          priorMonth.getUTCFullYear(),
          priorMonth.getUTCMonth() + 1,
          RN_24_7_MONTHLY_REPORT_DAY,
        ),
      ).toISOString(),
      kind: 'STATUTORY',
      provisionOrTargetLabel: PROVISION,
      status: band === 'OVERDUE' ? 'OVERDUE' : 'DUE_SOON',
      href: facilityId
        ? `/compliance/rn-coverage?facilityId=${encodeURIComponent(facilityId)}&month=${periodKey}`
        : '/compliance/rn-coverage',
      facilityId,
      meta: { dayOfMonth, daysOverdue, priorMonthLabel, tz: labelTz },
    },
  };
}

/**
 * Legacy behaviour: UTC calendar day-of-month, platform-wide (no tenant
 * timezone). Retained for compatibility; new reminders use the home clock.
 */
export function evaluateRn247MonthlyReportLegacyUtc(tenantId: string, now: Date): Rn247Evaluation {
  const dayOfMonth = now.getUTCDate();
  const priorMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const priorMonthLabel = priorMonth.toLocaleString('en-AU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return buildEvaluation(tenantId, dayOfMonth, priorMonth, priorMonthLabel, 'UTC');
}

/**
 * Day-of-month resolved in the facility's own timezone. The cron and
 * worklist pass facilityId for a stable per-home occurrence and deep link.
 */
export function evaluateRn247MonthlyReportTenantAware(
  tenantId: string,
  now: Date,
  tz: string,
  facilityId?: string,
): Rn247Evaluation {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const year = Number(parts.find((p) => p.type === 'year')!.value);
  const month = Number(parts.find((p) => p.type === 'month')!.value); // 1-12
  const dayOfMonth = Number(parts.find((p) => p.type === 'day')!.value);
  const priorMonth = new Date(Date.UTC(year, month - 2, 1));
  const priorMonthLabel = priorMonth.toLocaleString('en-AU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return buildEvaluation(tenantId, dayOfMonth, priorMonth, priorMonthLabel, tz, facilityId);
}
